import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Safe localStorage wrapper
const safeStorage = {
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('localStorage removeItem error:', e);
    }
  }
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const { processSession } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        setStatus('processing');
        
        // Get session_id from URL hash
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id found in URL hash');
          setError('Oturum bilgisi bulunamadı');
          setStatus('error');
          setTimeout(() => navigate('/giris', { replace: true }), 2000);
          return;
        }

        setStatus('authenticating');
        
        // Process the session - this will save the token to localStorage
        const user = await processSession(sessionId);

        if (!user) {
          throw new Error('Kullanıcı bilgisi alınamadı');
        }

        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);
        
        setStatus('success');
        
        // Navigate to home after a brief delay
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 500);
        
      } catch (error) {
        console.error('Auth callback error:', error);
        setError(error.message || 'Giriş işlemi başarısız');
        setStatus('error');
        
        // Clear any partial state
        safeStorage.removeItem('session_token');
        safeStorage.removeItem('auth_timestamp');
        
        setTimeout(() => navigate('/giris', { replace: true }), 2000);
      }
    };

    processAuth();
  }, [navigate, processSession]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8 bg-white rounded-lg shadow-sm max-w-sm">
        {status === 'error' ? (
          <>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-600 font-medium mb-2">Giriş Başarısız</p>
            <p className="text-gray-500 text-sm">{error}</p>
            <p className="text-gray-400 text-xs mt-4">Giriş sayfasına yönlendiriliyorsunuz...</p>
          </>
        ) : status === 'success' ? (
          <>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-green-600 font-medium">Giriş Başarılı!</p>
            <p className="text-gray-500 text-sm mt-2">Ana sayfaya yönlendiriliyorsunuz...</p>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">
              {status === 'authenticating' ? 'Doğrulanıyor...' : 'Giriş yapılıyor...'}
            </p>
            <p className="text-gray-400 text-sm mt-2">Lütfen bekleyin</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
